import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import "./creator.css";
import { api, fmtDate, getToken, login, qrUrl, usd } from "./api";
import { CreatorBar, CreatorFooter } from "./CreatorLanding";
import HaileyPanel from "./HaileyPanel";
import { TokenVerify } from "../redux/actions/User";

const TABS = [
  ["overview", "Overview"],
  ["events", "Events"],
  ["qr", "My QR codes"],
  ["sales", "Sales"],
  ["earnings", "Earnings"],
  ["hailey", "HAILEY"],
  ["account", "Account"],
];

const STATUS_COPY = {
  PENDING: "Your application is in review. You'll get an email as soon as you're approved — then your links and QR codes unlock.",
  REJECTED: "Your application wasn't approved this time.",
  SUSPENDED: "Your account is paused, so your links aren't tracking right now. Contact admin@instapass.shop with questions.",
};

function useToast() {
  const [msg, setMsg] = useState("");
  const show = useCallback((m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 2200);
  }, []);
  return [msg, show];
}

let PROMO = null; // the creator's TEVO promo code, set once the dashboard loads
function caption(event, url) {
  return `I'm going to ${event.name}${event.date ? ` on ${fmtDate(event.date, { month: "short", day: "numeric" })}` : ""} — grab tickets with my InstaPass link: ${url}${PROMO ? ` or use code ${PROMO} at checkout` : ""} #ad (I earn a commission on tickets bought with my link or code)`;
}

function PromoBlock({ creator, toast }) {
  if (!creator.promoCode) {
    return (
      <div className="cp-note" style={{ marginBottom: 24 }}>
        Your promo code is being set up by the InstaPass team. Until then, share your links and QR codes — those are credited to you too.
      </div>
    );
  }
  const off = creator.promo ? (creator.promo.percentage ? `${creator.promo.value}% off` : `$${creator.promo.value} off`) : null;
  return (
    <div className="cp-receipt" style={{ marginBottom: 28, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div className="head" style={{ marginBottom: 6 }}>Your promo code{off ? ` · gives fans ${off}` : ""}</div>
        <div className="figure" style={{ fontSize: "clamp(30px, 8vw, 44px)" }}>{creator.promoCode}</div>
        <div className="cp-fine" style={{ margin: "8px 0 0" }}>Every ticket order with this code is credited to you, on any event open to creators, from any device.</div>
      </div>
      <button
        className="cp-btn small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(creator.promoCode);
            toast("Promo code copied");
          } catch {
            toast("Couldn't copy — long-press the code instead");
          }
        }}
      >
        Copy code
      </button>
    </div>
  );
}

function ShareTools({ event, link, toast }) {
  const copy = async (text, done) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(done);
    } catch {
      toast("Couldn't copy — long-press the link instead");
    }
  };
  const share = async () => {
    const text = caption(event, link.url);
    if (navigator.share) {
      try {
        await navigator.share({ title: event.name, text, url: link.url });
      } catch {}
    } else copy(text, "Caption copied — paste it into TikTok or Instagram");
  };
  return (
    <>
      <button className="cp-btn small" type="button" onClick={share}>
        Share
      </button>
      <button className="cp-btn small ghost" type="button" onClick={() => copy(link.url, "Link copied")}>
        Copy link
      </button>
      <button className="cp-btn small ghost" type="button" onClick={() => copy(caption(event, link.url), "Caption with #ad copied")}>
        Copy caption
      </button>
      <a className="cp-btn small ghost" href={`sms:?&body=${encodeURIComponent(caption(event, link.url))}`}>
        Text it
      </a>
    </>
  );
}

function SignIn({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="cp-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
          await login(email, password);
          onDone();
        } catch (err) {
          setError(err.message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h1 className="display" style={{ fontSize: "clamp(28px, 6vw, 44px)", margin: 0 }}>
        Creator sign in
      </h1>
      <div className="cp-field">
        <label htmlFor="si-e">Email</label>
        <input id="si-e" className="cp-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      <div className="cp-field">
        <label htmlFor="si-p">Password</label>
        <input id="si-p" className="cp-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </div>
      {error && <div className="cp-error" role="alert">{error}</div>}
      <div className="cp-actions">
        <button className="cp-btn" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/creators/apply" className="cp-link">
          Not a Creator Partner yet? Apply
        </Link>
      </div>
      <Link to="/forgotpassword" className="cp-link">
        Forgot your password?
      </Link>
    </form>
  );
}

export default function CreatorDashboard() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: true });
  const [tab, setTab] = useState(new URLSearchParams(location.search).get("tab") || "overview");
  const [events, setEvents] = useState(null);
  const [links, setLinks] = useState(null);
  const [sales, setSales] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [toastMsg, toast] = useToast();

  const load = useCallback(async () => {
    if (!getToken()) return setState({ loading: false, signedOut: true });
    try {
      const me = await api("/me");
      setState({ loading: false, ...me });
    } catch (err) {
      if (err.status === 404) setState({ loading: false, notCreator: true });
      else if (err.status === 400 || err.status === 401) setState({ loading: false, signedOut: true });
      else setState({ loading: false, error: err.message });
    }
  }, []);

  useEffect(() => {
    document.title = "Creator dashboard — InstaPass";
    load();
  }, [load]);

  const creator = state.creator;
  const approved = creator && ["APPROVED", "ACTIVE"].includes(creator.status);

  useEffect(() => {
    if (!creator) return;
    if (tab === "events" || tab === "overview") api("/events").then((r) => setEvents(r.events)).catch(() => setEvents([]));
    if (tab === "qr" || tab === "overview") api("/links").then((r) => setLinks(r.links)).catch(() => setLinks([]));
    if (tab === "sales" || tab === "earnings") api("/sales").then((r) => setSales(r.sales)).catch(() => setSales([]));
    if (tab === "earnings") api("/payouts").then((r) => setPayouts(r.payouts)).catch(() => setPayouts([]));
  }, [tab, creator]);

  const getLink = async (eventId) => {
    try {
      const r = await api("/links", { method: "POST", body: { eventId } });
      setEvents((list) => list.map((e) => (e.id === eventId ? { ...e, link: r.link } : e)));
      setLinks(null);
      toast("Your link and QR code are ready");
    } catch (err) {
      toast(err.message);
    }
  };

  const go = (t) => {
    setTab(t);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const bar = (
    <CreatorBar
      right={
        creator ? (
          <span className="cp-status" title="Account status">
            {creator.creatorId}
          </span>
        ) : null
      }
    />
  );

  if (state.loading)
    return (
      <div className="cp">
        {bar}
        <p className="cp-wrap cp-fine" style={{ paddingTop: 40 }}>
          Loading your dashboard…
        </p>
      </div>
    );

  if (state.signedOut)
    return (
      <div className="cp">
        {bar}
        <main className="cp-wrap" style={{ paddingTop: 36, paddingBottom: 72 }}>
          <SignIn
            onDone={() => {
              dispatch(TokenVerify());
              setState({ loading: true });
              load();
            }}
          />
        </main>
        <CreatorFooter />
      </div>
    );

  if (state.notCreator || state.error)
    return (
      <div className="cp">
        {bar}
        <main className="cp-wrap" style={{ paddingTop: 36, paddingBottom: 72 }}>
          <div className="cp-empty">
            <h3>{state.error ? "We couldn't load your dashboard" : "This account isn't a Creator Partner yet"}</h3>
            <p style={{ margin: "0 0 16px" }}>{state.error || "Apply with this account and you'll be reviewed by our team."}</p>
            {state.error ? (
              <button className="cp-btn" onClick={load}>
                Try again
              </button>
            ) : (
              <Link to="/creators/apply" className="cp-btn">
                Become a Creator Partner
              </Link>
            )}
          </div>
        </main>
      </div>
    );

  const s = state.stats;
  const firstName = creator.fullName.split(" ")[0];
  PROMO = creator.promoCode || null;

  return (
    <div className="cp">
      {bar}
      <div className="cp-wrap cp-dash-head">
        <h1 className="display">Welcome back, {firstName}</h1>
        <p>{approved ? "Your influence is working." : STATUS_COPY[creator.status]}</p>
      </div>

      <nav className="cp-tabs" aria-label="Creator dashboard">
        <div className="cp-wrap" role="tablist">
          {TABS.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} className="cp-tab" onClick={() => go(id)}>
              {label}
            </button>
          ))}
        </div>
      </nav>

      <main className="cp-wrap cp-panel">
        {new URLSearchParams(location.search).get("welcome") && creator.status === "PENDING" && tab === "overview" && (
          <div className="cp-note" style={{ marginBottom: 24 }}>
            Application received. Your creator ID is <b>{creator.creatorId}</b>. We emailed a confirmation to {creator.email}.
          </div>
        )}

        {tab === "overview" && (
          <>
            {approved && <PromoBlock creator={creator} toast={toast} />}
            <div className="cp-hero-figure">
              <span className="figure">{usd(s.commissionsEarnedCents)}</span>
              <span className="label">Total commissions earned (approved and paid)</span>
            </div>
            <div className="cp-ledger">
              <div>
                Total QR scans <b>{s.qrScans}</b>
              </div>
              <div>
                Total referral clicks <b>{s.referralClicks}</b>
              </div>
              <div>
                Total tickets sold <b>{s.ticketsSold}</b>
              </div>
              <div>
                Pending commissions <b>{usd(s.pendingCommissionsCents)}</b>
              </div>
              <div>
                Available for payout <b>{usd(s.availablePayoutCents)}</b>
              </div>
              <div>
                Total payouts <b>{usd(s.totalPayoutsCents)}</b>
              </div>
              <div>
                Orders <b>{s.orders}</b>
              </div>
            </div>
            <p className="cp-fine">All figures come from verified InstaPass orders. A scan or click isn't a sale.</p>

            <div style={{ marginTop: 40 }}>
              {approved ? (
                events && events.length ? (
                  <>
                    <h2 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 16px" }}>Promote next</h2>
                    <EventGrid events={events.slice(0, 3)} canGenerate onGetLink={getLink} toast={toast} onQr={() => go("qr")} />
                  </>
                ) : (
                  <div className="cp-empty">
                    <h3>No events open right now</h3>
                    New events show up here as InstaPass authorizes them. You'll get an email when they drop.
                  </div>
                )
              ) : (
                <div className="cp-empty">
                  <h3>While you wait</h3>
                  Ask HAILEY how commissions work, or browse the events creators are promoting right now.
                  <div className="cp-actions" style={{ marginTop: 16 }}>
                    <button className="cp-btn small" onClick={() => go("hailey")}>
                      Ask HAILEY
                    </button>
                    <button className="cp-btn small ghost" onClick={() => go("events")}>
                      See events
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {tab === "events" && (
          <>
            {!approved && <div className="cp-note" style={{ marginBottom: 20 }}>Your links unlock once you're approved.</div>}
            {events == null ? (
              <p className="cp-fine">Loading events…</p>
            ) : events.length ? (
              <EventGrid events={events} canGenerate={approved} onGetLink={getLink} toast={toast} onQr={() => go("qr")} />
            ) : (
              <div className="cp-empty">
                <h3>No events open right now</h3>
                New events show up here as InstaPass authorizes them.
              </div>
            )}
          </>
        )}

        {tab === "qr" && (
          <>
            {approved && <PromoBlock creator={creator} toast={toast} />}
            {links == null ? (
              <p className="cp-fine">Loading your codes…</p>
            ) : links.length ? (
              <>
                <p className="cp-fine" style={{ marginTop: 0 }}>
                  These are marketing codes that send fans to the event page. They aren't admission tickets — buyers get their own ticket
                  QR at checkout.
                </p>
                {links.map((l) => (
                  <div className="cp-qr-row" key={l.code}>
                    <img src={qrUrl(l.code, "png")} alt={`QR code for ${l.event.name}`} />
                    <div>
                      <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{l.event.name}</h3>
                      <div className="cp-fine" style={{ margin: "4px 0 0" }}>
                        {fmtDate(l.event.date)} · {l.scans} scans · {l.clicks} clicks {!l.active && "· paused"}
                      </div>
                      <div className="url">{l.url}</div>
                      <div className="cp-actions">
                        <a className="cp-btn small" href={qrUrl(l.code, "png", true)} download>
                          Download PNG
                        </a>
                        <a className="cp-btn small ghost" href={qrUrl(l.code, "svg", true)} download>
                          Download SVG
                        </a>
                        <ShareTools event={l.event} link={l} toast={toast} />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="cp-empty">
                <h3>No codes yet</h3>
                {approved ? "Pick an event and tap “Get my code” — your QR and link are created instantly." : "Your codes unlock once you're approved."}
                {approved && (
                  <div style={{ marginTop: 16 }}>
                    <button className="cp-btn small" onClick={() => go("events")}>
                      Choose an event
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {tab === "sales" && (
          <>
            {sales == null ? (
              <p className="cp-fine">Loading sales…</p>
            ) : sales.length ? (
              <div className="cp-table-scroll">
                <table className="cp-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Event</th>
                      <th>Tickets</th>
                      <th>Commission</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((r) => (
                      <tr key={r.id}>
                        <td>{fmtDate(r.date)}</td>
                        <td>{r.event}</td>
                        <td>{r.quantity}</td>
                        <td>{r.commission ? usd(r.commission.amountCents) : "—"}</td>
                        <td>
                          {r.commission ? <span className={`cp-status ${r.commission.status}`}>{r.commission.status}</span> : <span className="cp-status void">{r.note}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="cp-empty">
                <h3>No sales yet</h3>
                Sales appear here as soon as someone completes a purchase through your link or QR code.
              </div>
            )}
          </>
        )}

        {tab === "earnings" && (
          <>
            <div className="cp-ledger">
              <div>
                Pending <b>{usd(s.pendingCommissionsCents)}</b>
              </div>
              <div>
                Approved, not yet paid <b>{usd(s.availablePayoutCents)}</b>
              </div>
              <div>
                Total earned <b>{usd(s.commissionsEarnedCents)}</b>
              </div>
              <div>
                Paid out <b>{usd(s.totalPayoutsCents)}</b>
              </div>
            </div>
            <div className="cp-note" style={{ margin: "20px 0 32px" }}>
              <b>Pending</b> means a sale through your link is verified but still in review — we check for refunds and cancellations
              first. <b>Approved</b> means it's cleared for your next payout. Before your first payout, InstaPass will collect your
              payment and tax details.
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 12px" }}>Payouts</h2>
            {payouts == null ? (
              <p className="cp-fine">Loading payouts…</p>
            ) : payouts.length ? (
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id}>
                      <td>{fmtDate(p.paidAt)}</td>
                      <td>{usd(p.amountCents)}</td>
                      <td>{p.method}</td>
                      <td>
                        <span className={`cp-status ${p.status}`}>{p.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="cp-fine">No payouts yet.</p>
            )}
          </>
        )}

        {tab === "hailey" && (
          <div style={{ maxWidth: 680 }}>
            <HaileyPanel
              signedIn
              onAction={(a) => {
                if (a.type === "navigate") go(a.to);
              }}
            />
          </div>
        )}

        {tab === "account" && <Account creator={creator} toast={toast} onChange={load} />}
      </main>
      <CreatorFooter />
      {toastMsg && (
        <div className="cp-toast" role="status">
          {toastMsg}
        </div>
      )}
    </div>
  );
}

function EventGrid({ events, canGenerate, onGetLink, toast, onQr }) {
  return (
    <div className="cp-events">
      {events.map((e) => (
        <article className="cp-event" key={e.id}>
          <div className="art" style={e.imageUrl ? { backgroundImage: `url(${e.imageUrl})` } : undefined}>
            {e.date && <span className="date">{fmtDate(e.date, { month: "short", day: "numeric" })}</span>}
          </div>
          <div className="body">
            <h3>{e.name}</h3>
            <div className="venue">{[e.venue, e.city].filter(Boolean).join(", ")}</div>
            <div className="rate">
              {e.rate ? (
                <>
                  You earn <b>{e.rate.label}</b>
                </>
              ) : (
                "Amount not set yet — sales are still tracked"
              )}
            </div>
            {e.promoCopy && <div className="venue">{e.promoCopy}</div>}
            <div className="tools">
              {e.link ? (
                <>
                  <button className="cp-btn small light" onClick={onQr}>
                    View QR
                  </button>
                  <ShareTools event={e} link={e.link} toast={toast} />
                </>
              ) : (
                <button className="cp-btn small" disabled={!canGenerate} onClick={() => onGetLink(e.id)}>
                  Get my code
                </button>
              )}
              {e.assets?.map((a) => (
                <a key={a.url} className="cp-btn small ghost" href={a.url} target="_blank" rel="noreferrer">
                  {a.label || "Artwork"}
                </a>
              ))}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function Account({ creator, toast, onChange }) {
  const [saving, setSaving] = useState(false);
  return (
    <div style={{ maxWidth: 560 }}>
      <div>
        <div className="cp-ledger" style={{ gridTemplateColumns: "1fr" }}>
          <div>
            Creator ID <b>{creator.creatorId}</b>
          </div>
          <div>
            Status <span className={`cp-status ${creator.status}`}>{creator.status}</span>
          </div>
          <div>
            Tier <b style={{ textTransform: "capitalize" }}>{creator.tier}</b>
          </div>
          <div>
            Email <b style={{ fontSize: 15 }}>{creator.email}</b>
          </div>
          <div>
            TikTok <b style={{ fontSize: 15 }}>{creator.tiktok ? `@${creator.tiktok}` : "—"}</b>
          </div>
          <div>
            Instagram <b style={{ fontSize: 15 }}>{creator.instagram ? `@${creator.instagram}` : "—"}</b>
          </div>
          <div>
            City <b style={{ fontSize: 15 }}>{creator.city}</b>
          </div>
        </div>
        <label className="cp-check" style={{ marginTop: 20 }}>
          <input
            type="checkbox"
            checked={creator.marketingConsent}
            disabled={saving}
            onChange={async (e) => {
              setSaving(true);
              try {
                await api("/preferences", { method: "POST", body: { marketingConsent: e.target.checked } });
                toast("Email preference saved");
                onChange();
              } catch (err) {
                toast(err.message);
              } finally {
                setSaving(false);
              }
            }}
          />
          <span>Program emails — new events, tips and performance updates. Every email also has an unsubscribe link.</span>
        </label>
      </div>
    </div>
  );
}
